import React, { useEffect, useRef, useState } from "react";
import "./WebLoader.css";

/** Default path — copy `src/Assets/jsons/loadingAnimation.json` to your web `public` folder. */
export const DEFAULT_LOADER_ANIMATION =
  require("../../appAssets/jsons/loadingAnimation.json") as string;

export type WebLoaderProps = Readonly<{
  /** Semi-transparent overlay instead of solid white. */
  isTransparent?: boolean;
  /** Cover the full viewport (default: true). */
  fullScreen?: boolean;
  /** Lottie JSON URL or animation data object. */
  animationSrc?: string | Record<string, unknown>;
  /** Animation container size in pixels. */
  size?: number;
  /** Extra class on the overlay root. */
  className?: string;
  /** Accessible loading label. */
  label?: string;
}>;

const WebLoader: React.FC<WebLoaderProps> = ({
  isTransparent = false,
  fullScreen = true,
  animationSrc = DEFAULT_LOADER_ANIMATION,
  size = 400,
  className = "",
  label = "Loading",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<{ destroy: () => void } | null>(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const lottieModule = await import("lottie-web");
        const lottie = lottieModule.default;
        if (cancelled || !containerRef.current) return;

        animationRef.current?.destroy();
        containerRef.current.innerHTML = "";

        const baseConfig = {
          container: containerRef.current,
          renderer: "svg" as const,
          loop: true,
          autoplay: true,
        };

        if (typeof animationSrc === "string") {
          const res = await fetch(animationSrc);
          if (!res.ok)
            throw new Error(`Failed to load animation (${res.status})`);
          if (cancelled || !containerRef.current) return;
          const animationData = await res.json();
          if (cancelled || !containerRef.current) return;
          animationRef.current = lottie.loadAnimation({
            ...baseConfig,
            animationData,
          });
        } else {
          animationRef.current = lottie.loadAnimation({
            ...baseConfig,
            animationData: animationSrc,
          });
        }
      } catch {
        if (!cancelled) setShowFallback(true);
      }
    };

    load();

    return () => {
      cancelled = true;
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, [animationSrc]);

  const overlayClass = [
    "yeti-loader-overlay",
    fullScreen
      ? "yeti-loader-overlay--fullscreen"
      : "yeti-loader-overlay--inline",
    isTransparent ? "yeti-loader-overlay--transparent" : "",
    showFallback ? "yeti-loader-fallback-active" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={overlayClass}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div
        ref={containerRef}
        className="yeti-loader-animation"
        style={{ width: size, height: size }}
      />
      <div className="yeti-loader-fallback" aria-hidden />
    </div>
  );
};

export default WebLoader;
